/**
 * CampaignsPage — Dashboard de Campañas Digitales (Cantabria Labs).
 *
 * Prueba de concepto: toma los reportes mensuales en Excel (servidos desde
 * /public, ver campaignsService) y los pinta como un tablero ejecutivo compacto.
 * Simula el flujo futuro: cada mes entra un Excel nuevo y el tablero se
 * actualiza solo. Todo el cómputo ocurre en el cliente; nada está hardcodeado.
 */
import React, { useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { Bar, Doughnut } from 'react-chartjs-2';
import { useOrganization } from '../../contexts/OrganizationContext';
import logoDefault from '../../assets/img/logo-data-new.png';
import {
  Chart as ChartJS, CategoryScale, LinearScale, BarElement, BarController,
  LineElement, LineController, PointElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import {
  FaBullhorn, FaSync, FaFilePdf, FaDollarSign, FaEye, FaMousePointer,
  FaChartLine, FaTag, FaHandPointer, FaLayerGroup, FaChartBar, FaBullseye,
  FaTrophy, FaSearch, FaSort, FaSortUp, FaSortDown, FaChevronLeft, FaChevronRight,
  FaSpinner, FaExclamationTriangle, FaArrowUp, FaArrowDown, FaTiktok, FaFileCsv,
} from 'react-icons/fa';
import { FaMeta } from 'react-icons/fa6';
import {
  loadCampaignReports, loadCampaignReportsFromMeta, loadCampaignReportsFromTikTok,
  loadCampaignReportsAll,
  summarize, groupBy, PLATFORM_COLORS, OBJECTIVE_COLORS,
} from '../../services/campaignsService';
import ExecutiveSummary from './ExecutiveSummary';
import { metaApi } from '../../services/metaService';
import MetaInsightsSections from './MetaInsightsSections';
import { AccountStrip, DailyTrendCard } from './MetaOverview';
import CampaignDrawer from './CampaignDrawer';
import { makeMoney, OBJECTIVE_LABEL } from './metaAdsShared';
import './CampaignsPage.css';
import './ExecutiveSummary.css';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, BarController, LineElement,
  LineController, PointElement, ArcElement, Tooltip, Legend, Filler,
);
ChartJS.defaults.font.family = "'Roboto', system-ui, -apple-system, sans-serif";
ChartJS.defaults.color = '#6b7a90';

// ── Formateadores ──────────────────────────────────────────────────────────
// La divisa sale de la cuenta publicitaria (Meta la informa); MXN solo es el
// valor inicial. Es de módulo porque las opciones de Chart.js de abajo también
// formatean dinero y viven fuera del componente.
let moneyCurrency = 'MXN';
let moneyFmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', maximumFractionDigits: 0 });
function setMoneyCurrency(cur) {
  if (!cur || cur === moneyCurrency) return;
  try {
    moneyFmt = new Intl.NumberFormat('es-MX', { style: 'currency', currency: cur, maximumFractionDigits: 0 });
    moneyCurrency = cur;
  } catch (_) { /* divisa desconocida: se queda la anterior */ }
}
const numFmt = new Intl.NumberFormat('es-MX');
const compact = (n) => new Intl.NumberFormat('es-MX', { notation: 'compact', maximumFractionDigits: 1 }).format(n || 0);
const money = (n) => moneyFmt.format(Math.round(n || 0));
const moneyCompact = (n) => '$' + compact(n);
const pct = (n, d = 2) => `${((n || 0) * 100).toFixed(d)}%`;
const PLATFORM_ORDER = ['Meta', 'TikTok', 'YouTube', 'Banners', 'Otros'];
const MONTH_ABBR = { Enero: 'Ene', Febrero: 'Feb', Marzo: 'Mar', Abril: 'Abr', Mayo: 'May', Junio: 'Jun', Julio: 'Jul', Agosto: 'Ago', Septiembre: 'Sep', Octubre: 'Oct', Noviembre: 'Nov', Diciembre: 'Dic' };
const PER_PAGE_OPTS = [10, 25, 50];

// Etiqueta honesta de un tramo mensual: "Ago" si cubre el mes completo, "26–31
// ago" si es parcial. Con "Últimos 30 días" (o el mes en curso) los tramos no
// son meses completos, y una barra rotulada "Ago" con solo 6 días de gasto se
// leía como "en agosto casi no se invirtió". Solo para Meta: ahí start/end son
// el tramo consultado; en el Excel son las fechas de la campaña.
function spanLabel(month, start, end) {
  const abbr = MONTH_ABBR[month] || month;
  if (!start || !end) return abbr;
  const [y, m] = String(start).split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const d1 = Number(String(start).slice(8, 10));
  const d2 = Number(String(end).slice(8, 10));
  if (!d1 || !d2 || (d1 === 1 && d2 === last)) return abbr;
  const low = abbr.toLowerCase();
  return d1 === d2 ? `${d1} ${low}` : `${d1}–${d2} ${low}`;
}

const statusClass = (s) => {
  const t = (s || '').toLowerCase();
  if (t.includes('activ')) return 'ok';
  if (t.includes('material') || t.includes('pend') || t.includes('tbd')) return 'warn';
  return 'off';
};
const paceColor = (p) => (p >= 0.95 && p <= 1.05 ? '#10b981' : p > 1.05 ? '#ef4444' : '#f59e0b');
// Con presupuesto TOTAL, Meta carga el gasto al inicio: pasarse del reparto
// parejo en un mes no es sobregasto, así que no se pinta de rojo.
const LIFETIME_PACE_HINT = 'Presupuesto total de campaña: Meta no lo gasta parejo por día (suele cargar el inicio). Se compara contra un reparto parejo; pasarse en un mes no significa pasarse del presupuesto.';
const rowPaceColor = (r) => (r.budgetType === 'lifetime' && r.index > 1.05 ? '#3b82f6' : paceColor(r.index));

// Claves de mes 'YYYY-MM' desplazadas n meses (negativo = hacia atrás).
const shiftKey = (key, n) => {
  const [y, m] = key.split('-').map(Number);
  const d = new Date(y, m - 1 + n, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthsBetween = (a, b) => {
  const [ya, ma] = a.split('-').map(Number);
  const [yb, mb] = b.split('-').map(Number);
  return (yb - ya) * 12 + (mb - ma) + 1;
};
const COMPARE_OPTS = [
  { id: 'prev', label: 'Periodo anterior', short: 'periodo ant.' },
  { id: 'yoy', label: 'Mismo periodo del año anterior', short: 'año ant.' },
  { id: 'none', label: 'Sin comparar', short: '' },
];

// Prefijo común de los nombres de campaña. Las agencias nombran con una
// nomenclatura larga ("MXN_HM_CANTABRIA LABS_CANTABRIA LABS_...") y la parte que
// distingue una campaña de otra queda al final: truncado con elipsis, todas las
// filas se veían iguales. Se recorta hasta el último separador del prefijo.
function commonPrefix(names) {
  const list = names.filter(Boolean);
  if (list.length < 2) return '';
  let pre = list[0];
  for (const n of list) {
    let i = 0;
    while (i < pre.length && i < n.length && pre[i] === n[i]) i++;
    pre = pre.slice(0, i);
    if (!pre) return '';
  }
  const cut = Math.max(pre.lastIndexOf('_'), pre.lastIndexOf(' '), pre.lastIndexOf('|'), pre.lastIndexOf('-'));
  return cut >= 7 ? pre.slice(0, cut + 1) : '';
}
const stripPrefix = (name, pre) => (pre && name && name.startsWith(pre) && name.length > pre.length ? name.slice(pre.length) : name);
// Unidad corta del resultado para la tabla (el nombre completo va en el tooltip).
const RESULT_UNIT = {
  'Personas alcanzadas': 'alcance', Impresiones: 'impr.', 'Clics en el enlace': 'clics',
  'Vistas de la página de destino': 'visitas', Interacciones: 'interac.', ThruPlays: 'thruplays',
  'Reproducciones de video': 'reprod.', Prospectos: 'prospectos', Compras: 'compras',
  'Conversaciones iniciadas': 'conversac.', 'Me gusta de la Página': 'me gusta',
};

// Resultado dominante de un grupo de filas: los resultados solo se suman entre
// filas del MISMO tipo (interacciones con interacciones), así que se toma el
// tipo con más inversión del grupo y su costo sale de la inversión de esas filas.
function dominantResult(rows) {
  const by = new Map();
  rows.forEach((r) => {
    if (!r.resultLabel || !r.results) return;
    const cur = by.get(r.resultLabel) || { label: r.resultLabel, perMil: r.resultPerMil, results: 0, spend: 0 };
    cur.results += r.results;
    cur.spend += r.spend || 0;
    by.set(r.resultLabel, cur);
  });
  const best = [...by.values()].sort((a, b) => b.spend - a.spend)[0];
  if (!best) return null;
  return { ...best, cost: best.results ? (best.spend / best.results) * (best.perMil ? 1000 : 1) : null };
}

// ── Rango de fechas: helpers de periodo ──────────────────────────────────────
// La fuente es mensual (un Excel por mes), así que el grano real es el mes. Un
// mes se identifica por su clave 'YYYY-MM'; estas utilidades lo mapean a fechas
// de calendario para poder cruzarlo contra un rango manual desde–hasta.
const firstDayOf = (key) => `${key}-01`;
const lastDayOf = (key) => {
  const [y, m] = key.split('-').map(Number);
  return `${key}-${String(new Date(y, m, 0).getDate()).padStart(2, '0')}`;
};
const monthSpanDates = (key) => {
  const [y, m] = key.split('-').map(Number);
  return [new Date(y, m - 1, 1), new Date(y, m, 0)];
};
// Presets de rango alineados al grano mensual real (nada de día/semana: no hay
// datos diarios en la fuente, mostrarlos sería inventar cifras).
// Ventana que se le pide a Meta cuando la fuente es la API. El filtro de periodo
// de esta pagina es CLIENTE (recorta sobre lo ya cargado), asi que si se pidieran
// solo 6 meses no habria forma de mirar mas atras: el selector se quedaria sin
// meses que ofrecer. Meta conserva ~37 meses; el backend recorta y avisa.
const META_MONTHS_BACK = 36;
function metaWideWindow() {
  const to = new Date();
  const from = new Date();
  from.setMonth(from.getMonth() - META_MONTHS_BACK);
  from.setDate(1);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: iso(from), to: iso(to) };
}

// Ventana de "últimos N días" (Meta). A diferencia del resto de periodos, que
// son meses completos, esta se le pide a la API con fechas exactas: Meta
// recorta las filas mensuales al rango, así que los totales son de esos días.
const isoDay = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function dayWindow(days, offsetDays = 0, yearsBack = 0) {
  const to = new Date();
  to.setDate(to.getDate() - offsetDays);
  to.setFullYear(to.getFullYear() - yearsBack);
  const from = new Date(to);
  from.setDate(from.getDate() - (days - 1));
  return { from: isoDay(from), to: isoDay(to) };
}
const DAY_PRESETS = [7, 30, 90];
const DEFAULT_META_DAYS = 30;

// Fecha de corte 'YYYY-MM-DD' → '28 feb 2026' (legible para quien no es técnico).
const cutoffLabel = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || '');
  if (!m) return iso;
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }).replace('.', '');
};

// Redes del selector principal: el icono identifica la red antes de leer la etiqueta.
const NETWORK_ICONS = { meta: <FaMeta />, tiktok: <FaTiktok /> };

const PERIOD_PRESETS = [
  { id: 'last1', label: 'Último mes' },
  { id: 'last3', label: 'Últimos 3 meses' },
  { id: 'quarter', label: 'Trimestre' },
  { id: 'year', label: 'Año' },
];

/**
 * networks + onNetworkChange (opcionales): cuando la pantalla agrupa varias
 * redes publicitarias (Campañas (API): Meta y TikTok) se pinta el selector de
 * red como PRIMER filtro. Es el de mayor jerarquía porque cambia la fuente de
 * datos completa; cuenta y periodo dependen de él y van después.
 */
export default function CampaignsPage({ source = 'excel', username = null, networks = null, onNetworkChange = null } = {}) {
  const isMeta = source === 'meta';
  const isTikTok = source === 'tiktok';
  const isAll = source === 'all';                 // todas las redes (demo)
  const isLive = isMeta || isTikTok || isAll;     // fuente en vivo (API) vs Excel
  // Con una sola red los chips de "Plataforma" solo repetirían el nombre de la
  // red (Todas / Meta): se ocultan para no sumar un filtro que no filtra nada.
  const singleSource = isMeta || isTikTok;
  const liveLabel = isAll ? 'Todas las redes' : isTikTok ? 'TikTok' : 'Meta';
  const showNetworkSwitch = Array.isArray(networks) && networks.length > 1 && typeof onNetworkChange === 'function';
  const { logoUrl, displayName } = useOrganization();
  // errorKind: 'empty' (la red respondió sin campañas) o 'fail' (no respondió).
  const [state, setState] = useState({ loading: true, error: null, errorKind: null, records: [], months: [], errors: [], brand: '' });
  // Periodo seleccionado — única fuente de verdad del filtro temporal.
  //   { mode: 'all' }                      → todos los meses
  //   { mode: 'month', key }               → un mes puntual (pestañas)
  //   { mode: 'range', preset, from, to }  → rango (preset o manual); from/to = 'YYYY-MM-DD'
  //   { mode: 'days', days }               → últimos N días exactos (solo Meta)
  // Meta abre en los últimos 30 días: es la lectura operativa del día a día; el
  // histórico completo sigue a un clic en "Todos los meses".
  const [period, setPeriod] = useState(() => (source === 'meta' ? { mode: 'days', days: DEFAULT_META_DAYS } : { mode: 'all' }));
  // Comparar contra: 'prev' (periodo anterior), 'yoy' (año anterior) o 'none'.
  const [compare, setCompare] = useState('prev');
  // Actualizar: sube el contador y las cargas de Meta saltan la caché del backend.
  const [refreshTick, setRefreshTick] = useState(0);
  const [platform, setPlatform] = useState('all');
  const [objFilter, setObjFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({ key: 'spend', dir: 'desc' });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [exporting, setExporting] = useState(false);

  // Cuentas publicitarias de la organizacion. Hay mas de una cuando una agencia
  // externa pauta sobre la marca y la cuenta propia queda como historico. NO se
  // suman: el alcance y la frecuencia no son aditivos entre cuentas (la misma
  // persona puede ser alcanzada por las dos), asi que se consulta una a la vez.
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  useEffect(() => {
    if (!isMeta || !username) return undefined;
    let cancelled = false;
    metaApi.adsAccounts(username)
      .then((r) => {
        if (cancelled) return;
        const list = r.accounts || [];
        setAccounts(list);
        // La primera cuenta SIN error: si la de por defecto perdió el acceso, abrir
        // en ella dejaría la pantalla en blanco teniendo otra que sí responde.
        const firstOk = list.find((a) => !a.error) || list[0];
        setAccount((cur) => cur || (firstOk ? firstOk.id : null));
      })
      .catch(() => { /* sin selector: se usa la cuenta por defecto del backend */ });
    return () => { cancelled = true; };
  }, [isMeta, username]);

  // Refs a las gráficas para capturarlas como imagen en el PDF
  const donutRef = useRef(null);
  const spendRef = useRef(null);
  const topRef = useRef(null);
  const clicksRef = useRef(null);
  const execRef = useRef(null); // handle imperativo del Resumen ejecutivo (para el PDF)

  // 1) Carga de los reportes: Excel mensuales (POC) o Meta Ads Insights en vivo.
  const load = useCallback(async (fresh = false) => {
    setState((s) => ({ ...s, loading: true, error: null, errorKind: null }));
    try {
      const res = isAll
        ? await loadCampaignReportsAll(username)
        : isTikTok
          ? await loadCampaignReportsFromTikTok(username)
          : isMeta
            ? await (async () => { const w = metaWideWindow(); return loadCampaignReportsFromMeta(username, w.from, w.to, account, fresh === true); })()
            : await loadCampaignReports();
      if (!res.records.length) {
        const emptyMsg = isAll
          ? 'No hay campañas para el periodo seleccionado.'
          : isLive
            ? `Sin datos de campañas en ${liveLabel} para el periodo. Verifica la conexión en Integraciones o el rango de fechas.`
            : 'No se encontraron datos en los reportes.';
        setState({ loading: false, error: emptyMsg, errorKind: 'empty', records: [], months: [], errors: res.errors, brand: res.brand });
      } else {
        setState({ loading: false, error: null, errorKind: null, ...res });
      }
    } catch (e) {
      setState((s) => ({ ...s, loading: false, error: String(e?.message || e), errorKind: 'fail', records: [], months: [] }));
    }
  }, [isMeta, isTikTok, isAll, isLive, liveLabel, username, account]);
  useEffect(() => { load(); }, [load]);

  const { records, months } = state;

  // ── Últimos N días (Meta) ───────────────────────────────────────────────────
  // Carga propia con fechas exactas (la del arranque trae 36 meses completos y
  // alimenta el selector de meses). Se pide también la ventana de comparación:
  // los N días previos o los mismos días del año anterior.
  const daysMode = isMeta && period.mode === 'days';
  const [daysData, setDaysData] = useState({ loading: false, error: null, records: [], months: [], errors: [], prev: null });
  const daysTickRef = useRef(0);
  useEffect(() => {
    if (!daysMode || !username) return undefined;
    let cancelled = false;
    const fresh = refreshTick !== daysTickRef.current;
    daysTickRef.current = refreshTick;
    const w = dayWindow(period.days);
    const pw = compare === 'none' ? null : compare === 'yoy' ? dayWindow(period.days, 0, 1) : dayWindow(period.days, period.days);
    setDaysData((st) => ({ ...st, loading: true, error: null }));
    Promise.all([
      loadCampaignReportsFromMeta(username, w.from, w.to, account, fresh),
      pw ? loadCampaignReportsFromMeta(username, pw.from, pw.to, account, fresh).catch(() => null) : Promise.resolve(null),
    ])
      .then(([cur, prev]) => {
        if (!cancelled) setDaysData({ loading: false, error: null, records: cur.records, months: cur.months, errors: cur.errors || [], prev: prev ? prev.records : null });
      })
      .catch((e) => {
        if (!cancelled) setDaysData({ loading: false, error: String(e?.message || e), records: [], months: [], errors: [], prev: null });
      });
    return () => { cancelled = true; };
  }, [daysMode, username, account, period.days, compare, refreshTick]);
  const viewRecords = daysMode ? daysData.records : records;
  const daysLoading = daysMode && daysData.loading;

  const platforms = useMemo(() => {
    const present = new Set(records.map((r) => r.platform));
    return PLATFORM_ORDER.filter((p) => present.has(p));
  }, [records]);

  // Meses incluidos según el periodo. En un rango manual se incluye un mes si su
  // span de calendario se solapa con [from, to]: el detalle es mensual, así que
  // no se recorta dentro de un mes; se toma el mes completo que toque el rango.
  const activeMonths = useMemo(() => {
    if (period.mode === 'days') return daysMode ? daysData.months : months;
    if (period.mode === 'all') return months;
    if (period.mode === 'month') return months.filter((m) => m.key === period.key);
    const from = period.from ? new Date(`${period.from}T00:00:00`) : null;
    const to = period.to ? new Date(`${period.to}T23:59:59`) : null;
    return months.filter((m) => {
      const [ms, me] = monthSpanDates(m.key);
      if (from && me < from) return false;
      if (to && ms > to) return false;
      return true;
    });
  }, [period, months, daysMode, daysData.months]);
  const activeKeys = useMemo(() => new Set(activeMonths.map((m) => m.key)), [activeMonths]);

  // ── Desgloses de Meta (demografia, ubicaciones, creativos, video) ──────────
  // Viven fuera del esquema mensual de `records`, asi que van en su propia
  // peticion. Con un periodo ACOTADO se pide exactamente ese rango, para que la
  // mitad de abajo del tablero hable del mismo periodo que la de arriba.
  //
  // Con "Todos los meses" NO se deriva el rango de los meses que trajo la tabla,
  // se pide la ventana completa. La diferencia importa: la tabla consulta a
  // nivel de CAMPANA y los desgloses a nivel de CUENTA, y no siempre devuelven
  // los mismos meses (una campana borrada deja gasto en la cuenta que la
  // consulta por campana ya no reporta). Derivar el rango de la tabla hacia
  // "todos" ocultaria justamente esa diferencia, que es la que permite
  // distinguir "no hubo pauta" de "la consulta por campana no la ve".
  const [explore, setExplore] = useState({ loading: false, error: null, data: null });
  const exploreTickRef = useRef(0);
  const focusTickRef = useRef(0);
  const metaRange = useMemo(() => {
    if (!isMeta) return null;
    if (period.mode === 'days') return dayWindow(period.days);
    if (period.mode !== 'all' && activeMonths.length) {
      const keys = activeMonths.map((m) => m.key).sort();
      return { from: firstDayOf(keys[0]), to: lastDayOf(keys[keys.length - 1]) };
    }
    return metaWideWindow();
  }, [isMeta, period.mode, period.days, activeMonths]);
  const metaFrom = metaRange?.from;
  const metaTo = metaRange?.to;
  useEffect(() => {
    if (!isMeta || !username || !metaFrom || !metaTo) return undefined;
    let cancelled = false;
    const fresh = refreshTick !== exploreTickRef.current;
    exploreTickRef.current = refreshTick;
    setExplore((s) => ({ ...s, loading: true, error: null }));
    metaApi.adsExplore(username, metaFrom, metaTo, account, { fresh })
      .then((d) => { if (!cancelled) setExplore({ loading: false, error: null, data: d }); })
      .catch((e) => { if (!cancelled) setExplore((s) => ({ ...s, loading: false, error: e.message || String(e) })); });
    // El cleanup evita que una respuesta lenta de un rango ya abandonado pise
    // a la del rango actual (el clasico race al cambiar de filtro rapido).
    return () => { cancelled = true; };
  }, [isMeta, username, metaFrom, metaTo, account, refreshTick]);

  // Campaña enfocada ("Analizar: campaña X"). Es una SEGUNDA carga, no un
  // reemplazo: la ficha de la cuenta, la salud de la entrega y el alcance
  // deduplicado de los KPIs siguen siendo de la cuenta completa.
  const [focus, setFocus] = useState(null);           // { id, name, resultKey }
  const [focusExplore, setFocusExplore] = useState({ loading: false, error: null, data: null });
  useEffect(() => { setFocus(null); }, [account]);
  useEffect(() => {
    if (!isMeta || !username || !metaFrom || !metaTo || !focus) {
      setFocusExplore({ loading: false, error: null, data: null });
      return undefined;
    }
    let cancelled = false;
    const fresh = refreshTick !== focusTickRef.current;
    focusTickRef.current = refreshTick;
    setFocusExplore((s) => ({ ...s, loading: true, error: null }));
    metaApi.adsExplore(username, metaFrom, metaTo, account, { campaign: focus.id, result: focus.resultKey, fresh })
      .then((d) => { if (!cancelled) setFocusExplore({ loading: false, error: null, data: d }); })
      .catch((e) => { if (!cancelled) setFocusExplore({ loading: false, error: e.message || String(e), data: null }); });
    return () => { cancelled = true; };
  }, [isMeta, username, metaFrom, metaTo, account, focus, refreshTick]);
  const analysis = focus ? focusExplore : explore;

  // Detalle de campaña (panel lateral), abierto desde la tabla.
  const [drawer, setDrawer] = useState(null);         // { id, name, resultKey }
  const analysisRef = useRef(null);
  const focusCampaign = useCallback((c) => {
    setFocus(c);
    setDrawer(null);
    if (c) setTimeout(() => analysisRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 60);
  }, []);

  // Divisa de la cuenta: primero la ficha de /explore, luego la del selector.
  const currency = explore.data?.account?.currency || accounts.find((a) => a.id === account)?.currency || 'MXN';
  if (isMeta) setMoneyCurrency(currency);
  const metaMoney = useMemo(() => makeMoney(currency), [currency]);
  const singleMonth = activeMonths.length === 1;
  const multiMonth = activeMonths.length !== 1;

  // Registros tras filtros globales (periodo + plataforma)
  const filtered = useMemo(() => viewRecords.filter((r) =>
    activeKeys.has(r.monthKey) &&
    (platform === 'all' || r.platform === platform)
  ), [viewRecords, activeKeys, platform]);

  // KPIs del periodo + comparación. "Periodo anterior" = los N meses de
  // calendario inmediatamente previos (un mes → el mes anterior; un trimestre →
  // el trimestre anterior); "año anterior" = los mismos meses un año atrás.
  // Con "Todos los meses" no hay contra qué comparar.
  const comparable = period.mode !== 'all' && activeMonths.length > 0;
  const kpis = useMemo(() => {
    const cur = summarize(filtered);
    let prev = null;
    if (daysMode) {
      const rows = (daysData.prev || []).filter((r) => platform === 'all' || r.platform === platform);
      if (compare !== 'none' && rows.length) prev = summarize(rows);
    } else if (comparable && compare !== 'none') {
      const keys = activeMonths.map((m) => m.key).sort();
      const span = monthsBetween(keys[0], keys[keys.length - 1]);
      const shift = compare === 'yoy' ? -12 : -span;
      const prevKeys = new Set(keys.map((k) => shiftKey(k, shift)));
      const rows = records.filter((r) => prevKeys.has(r.monthKey) && (platform === 'all' || r.platform === platform));
      if (rows.length) prev = summarize(rows);
    }
    return { cur, prev };
  }, [filtered, comparable, compare, activeMonths, records, platform, daysMode, daysData.prev]);
  const compareShort = daysMode && compare === 'prev' ? `${period.days} días ant.`
    : singleMonth && compare === 'prev' ? 'mes ant.' : (COMPARE_OPTS.find((o) => o.id === compare)?.short || '');

  // Alcance deduplicado (Meta): la suma de alcance de filas campaña×mes cuenta
  // dos veces a quien vio dos campañas o en dos meses. /explore lo trae sin
  // duplicar para el mismo periodo; mientras recarga se usa la suma.
  const dedupReach = isMeta && !explore.loading && explore.data?.totals?.reach != null ? explore.data.totals.reach : null;

  // Vistas POR MES (Resumen ejecutivo, real vs plan, clics por mes y la tabla).
  // Con "Últimos N días" usan los MESES COMPLETOS que toca el rango (agosto
  // entero y septiembre a la fecha) desde la carga histórica: un tramo de 6 días
  // de agosto rotulado junto a septiembre completo se leía como una caída de
  // inversión. Los KPIs, la serie diaria y el análisis siguen en los N días.
  const monthScopeRecords = daysMode ? records : viewRecords;
  const monthFiltered = useMemo(() => monthScopeRecords.filter((r) =>
    activeKeys.has(r.monthKey) && (platform === 'all' || r.platform === platform)
  ), [monthScopeRecords, activeKeys, platform]);

  // Tramo real de cada mes (primer y último día con datos consultados).
  const monthSpans = useMemo(() => {
    const map = {};
    if (!isMeta) return map;
    monthScopeRecords.forEach((r) => {
      const cur = map[r.monthKey] || { start: r.start, end: r.end };
      if (r.start && r.start < cur.start) cur.start = r.start;
      if (r.end && r.end > cur.end) cur.end = r.end;
      map[r.monthKey] = cur;
    });
    return map;
  }, [isMeta, monthScopeRecords]);
  const monthLabel = useCallback((m) => (isMeta
    ? spanLabel(m.month, monthSpans[m.key]?.start, monthSpans[m.key]?.end)
    : (MONTH_ABBR[m.month] || m.month)), [isMeta, monthSpans]);
  const rowMonthLabel = (r) => (isMeta ? spanLabel(r.month, r.start, r.end) : (MONTH_ABBR[r.month] || r.month));

  // Tendencia mensual del periodo activo (respeta plataforma)
  const trend = useMemo(() => activeMonths.map((m) => {
    const rows = monthScopeRecords.filter((r) => r.monthKey === m.key && (platform === 'all' || r.platform === platform));
    return { label: monthLabel(m), ...summarize(rows) };
  }), [activeMonths, monthScopeRecords, platform, monthLabel]);

  // Inversión por plataforma (respeta periodo, no plataforma → siempre el mix)
  const byPlatform = useMemo(() => {
    const base = viewRecords.filter((r) => activeKeys.has(r.monthKey));
    return groupBy(base, (r) => r.platform).sort((a, b) => b.spend - a.spend);
  }, [viewRecords, activeKeys]);

  // Inversión por objetivo (+ su resultado dominante, cuando la fuente lo trae)
  const byObjective = useMemo(() => groupBy(filtered, (r) => r.objective)
    .map((g) => ({ ...g, result: dominantResult(g.rows) }))
    .sort((a, b) => b.spend - a.spend), [filtered]);

  // Campañas del periodo con id de Meta: alimentan "Analizar" y el detalle.
  const metaCampaigns = useMemo(() => {
    if (!isMeta) return [];
    const by = new Map();
    filtered.forEach((r) => {
      if (!r.campaignId) return;
      const cur = by.get(r.campaignId) || { id: r.campaignId, name: r.campaign, label: r.campaign, resultKey: r.resultKey, spend: 0 };
      cur.spend += r.spend || 0;
      by.set(r.campaignId, cur);
    });
    return [...by.values()].sort((a, b) => b.spend - a.spend);
  }, [isMeta, filtered]);
  const metaCampaignOptions = useMemo(() => {
    const pre = commonPrefix(metaCampaigns.map((c) => c.name));
    return metaCampaigns.map((c) => ({ ...c, label: stripPrefix(c.name, pre) }));
  }, [metaCampaigns]);
  const hasResults = isMeta && filtered.some((r) => r.results != null);
  const campPrefix = useMemo(() => (isMeta ? commonPrefix([...new Set(records.map((r) => r.campaign))]) : ''), [isMeta, records]);
  const campName = useCallback((n) => stripPrefix(n, campPrefix), [campPrefix]);

  // Top 10 campañas por inversión
  const topCampaigns = useMemo(() => groupBy(filtered, (r) => r.campaign).sort((a, b) => b.spend - a.spend).slice(0, 10), [filtered]);

  // Agregado por campaña (para las gráficas "por campaña" del Resumen ejecutivo).
  const byCampaign = useMemo(() => groupBy(monthFiltered, (r) => r.campaign), [monthFiltered]);
  // Días de calendario del periodo activo (para "promedio diario").
  // Días que cubren las vistas por mes (para "promedio diario" del Resumen):
  // con meses completos, del primer día del primer mes a la fecha de corte.
  const periodDays = useMemo(() => {
    if (!daysMode) return activeMonths.reduce((a, m) => a + monthSpanDates(m.key)[1].getDate(), 0);
    return activeMonths.reduce((a, m) => {
      const sp = monthSpans[m.key];
      if (!sp?.start || !sp?.end) return a + monthSpanDates(m.key)[1].getDate();
      return a + Math.round((new Date(`${sp.end}T12:00:00`) - new Date(`${sp.start}T12:00:00`)) / 86400000) + 1;
    }, 0);
  }, [activeMonths, daysMode, monthSpans]);

  // Opciones de filtros de la tabla
  const objectiveOptions = useMemo(() => Array.from(new Set(monthFiltered.map((r) => r.objective))), [monthFiltered]);
  const statusOptions = useMemo(() => Array.from(new Set(monthFiltered.map((r) => r.status))), [monthFiltered]);

  // Tabla: filtros + búsqueda + orden
  const tableRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = monthFiltered.filter((r) =>
      (objFilter === 'all' || r.objective === objFilter) &&
      (statusFilter === 'all' || r.status === statusFilter));
    if (q) rows = rows.filter((r) =>
      r.campaign.toLowerCase().includes(q) || r.platform.toLowerCase().includes(q) || r.objectiveRaw.toLowerCase().includes(q));
    const { key, dir } = sort;
    const mul = dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = a[key], bv = b[key];
      if (typeof av === 'number' || typeof bv === 'number') return ((av || 0) - (bv || 0)) * mul;
      return String(av || '').localeCompare(String(bv || '')) * mul;
    });
  }, [monthFiltered, search, sort, objFilter, statusFilter]);

  const tableTotals = useMemo(() => summarize(tableRows), [tableRows]);

  // Reset de página al cambiar filtros
  useEffect(() => { setPage(1); }, [period, platform, search, objFilter, statusFilter, perPage]);

  // ── Presets y rango manual de periodo ──────────────────────────────────────
  const applyPreset = useCallback((id) => {
    if (!months.length) return;
    const last = months[months.length - 1];
    if (id === 'last1') { setPeriod({ mode: 'range', preset: id, from: firstDayOf(last.key), to: lastDayOf(last.key) }); return; }
    if (id === 'last3') { const s = months[Math.max(0, months.length - 3)]; setPeriod({ mode: 'range', preset: id, from: firstDayOf(s.key), to: lastDayOf(last.key) }); return; }
    if (id === 'quarter') {
      const [ly, lm] = last.key.split('-').map(Number);
      const qStart = Math.floor((lm - 1) / 3) * 3 + 1; // 1, 4, 7 o 10
      setPeriod({ mode: 'range', preset: id, from: `${ly}-${String(qStart).padStart(2, '0')}-01`, to: lastDayOf(last.key) });
      return;
    }
    if (id === 'year') { setPeriod({ mode: 'range', preset: id, from: `${last.year}-01-01`, to: lastDayOf(last.key) }); }
  }, [months]);

  const enterCustom = useCallback(() => {
    if (!months.length) return;
    setPeriod({ mode: 'range', preset: 'custom', from: firstDayOf(months[0].key), to: lastDayOf(months[months.length - 1].key) });
  }, [months]);
  const totalPages = Math.max(1, Math.ceil(tableRows.length / perPage));
  const pageSafe = Math.min(page, totalPages);
  const pageRows = useMemo(() => tableRows.slice((pageSafe - 1) * perPage, pageSafe * perPage), [tableRows, pageSafe, perPage]);

  const toggleSort = (key) => setSort((s) => s.key === key ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'desc' });
  const sortIcon = (key) => sort.key !== key ? <FaSort className="sort-ind" /> : sort.dir === 'asc' ? <FaSortUp className="sort-ind" /> : <FaSortDown className="sort-ind" />;

  const ready = !state.loading && !state.error && !daysLoading;

  const firstActive = activeMonths[0];
  const lastActive = activeMonths[activeMonths.length - 1];
  const periodLabel = daysMode
    ? `Últimos ${period.days} días (${cutoffLabel(metaRange.from)} – ${cutoffLabel(metaRange.to)})`
    : activeMonths.length === 0
    ? 'Sin datos en el rango'
    : singleMonth
      ? `${firstActive.month} ${firstActive.year}`
      : firstActive.year === lastActive.year
        ? `${firstActive.month} – ${lastActive.month} ${lastActive.year}`
        : `${firstActive.month} ${firstActive.year} – ${lastActive.month} ${lastActive.year}`;
  const cutoff = lastActive?.cutoff;
  // Nombre del periodo de las vistas por mes. En "Últimos N días" son meses
  // completos y se dice explícitamente, incluido hasta qué día va el mes en curso.
  const monthsPeriodLabel = (() => {
    if (!daysMode || !activeMonths.length) return periodLabel;
    const f = activeMonths[0];
    const l = activeMonths[activeMonths.length - 1];
    const names = f.key === l.key ? `${f.month} ${f.year}`
      : f.year === l.year ? `${f.month} – ${l.month} ${l.year}` : `${f.month} ${f.year} – ${l.month} ${l.year}`;
    const end = monthSpans[l.key]?.end;
    const lastDay = monthSpanDates(l.key)[1].getDate();
    const partial = end && Number(end.slice(8, 10)) < lastDay ? ` (${l.month.toLowerCase()} al ${Number(end.slice(8, 10))})` : '';
    return `Meses completos: ${names}${partial}`;
  })();
  // Límites de los selectores de fecha: acotados al rango real de reportes.
  const dataMin = months.length ? firstDayOf(months[0].key) : undefined;
  const dataMax = months.length ? lastDayOf(months[months.length - 1].key) : undefined;
  const c = kpis.cur;

  // ── Exportar a CSV ─────────────────────────────────────────────────────────
  // El detalle tal como está filtrado en la tabla, con TODAS las columnas que
  // la fuente trae (también las que la tabla no muestra por espacio). BOM UTF-8
  // para que Excel respete acentos al abrirlo con doble clic.
  const handleCsv = () => {
    const cols = [
      ['Mes', (r) => `${isMeta ? rowMonthLabel(r) : r.month} ${r.year}`],
      ['Plataforma', (r) => r.platform],
      ['Campaña', (r) => r.campaign],
      ['Objetivo', (r) => r.objectiveRaw || r.objective],
      ['Estado', (r) => r.status],
      ['Inversión', (r) => r.spend],
      ['Inversión planeada', (r) => r.spendPlanned],
      ['Ejecución vs plan', (r) => r.index],
      ['Impresiones', (r) => r.impressions],
      ['Alcance', (r) => r.reach],
      ['Frecuencia', (r) => r.frequency],
      ['Clics', (r) => r.clicks],
      ['CTR', (r) => r.ctr],
      ['CTR de salida', (r) => r.outboundCtr],
      ['Interacciones', (r) => r.interactions],
      ['Reproducciones de video', (r) => r.views],
      ['VTR', (r) => r.vtr],
    ];
    if (hasResults) {
      cols.push(['Tipo de resultado', (r) => r.resultLabel], ['Resultados', (r) => r.results],
        ['Costo por resultado', (r) => r.costPerResult], ['Valor de compra atribuido', (r) => r.purchaseValue]);
    }
    const cell = (v) => (v == null ? '' : typeof v === 'number' ? String(Math.round(v * 10000) / 10000) : `"${String(v).replace(/"/g, '""')}"`);
    const lines = [cols.map((c) => cell(c[0])).join(','), ...tableRows.map((r) => cols.map((c) => cell(c[1](r))).join(','))];
    const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `campanas_${(singleSource ? liveLabel : 'todas')}_${periodLabel}`.toLowerCase().replace(/[^a-z0-9áéíóúñ]+/gi, '_') + '.csv';
    document.body.appendChild(a);
    a.click();
    setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
  };

  // ── Exportar a PDF ─────────────────────────────────────────────────────────
  // Mismo patrón confiable que ReportPage: armamos un documento HTML completo
  // con membrete + gráficas capturadas como imágenes y lo imprimimos dentro de
  // un iframe oculto, así el navegador NO incluye el sidebar/chrome de la app y
  // el resultado queda paginado en A4.
  const handleExport = () => {
    const grab = (ref) => { try { return ref.current?.toBase64Image('image/png', 1) || ''; } catch (e) { return ''; } };
    const imgs = { donut: grab(donutRef), spend: grab(spendRef), top: grab(topRef), clicks: grab(clicksRef) };
    const exec = execRef.current?.getExport?.() || null; // 4 gráficas del Resumen ejecutivo

    const esc = (s) => String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
    const orgName = displayName || state.brand || 'Cantabria Labs';
    const rawLogo = logoUrl || logoDefault;
    const orgLogo = rawLogo && rawLogo.startsWith('/') ? `${window.location.origin}${rawLogo}` : rawLogo;
    const generatedDate = new Date().toLocaleString('es-MX', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    const platLabel = singleSource ? liveLabel : platform === 'all' ? 'Todas' : platform;

    const kpiCards = [
      { l: 'Gasto total', v: money(c.spend), s: `${pct(c.pacing, 1)} del plan` },
      { l: 'Impresiones', v: compact(c.impressions), s: `Alcance ${compact(c.reach)}` },
      { l: 'Clics', v: numFmt.format(c.clicks), s: `${c.campaigns} campañas` },
      { l: 'Tasa de clics (CTR)', v: pct(c.ctr), s: `${compact(c.interactions)} interacciones` },
      { l: 'Costo por mil impresiones (CPM)', v: money(c.cpm), s: 'promedio del periodo' },
      { l: 'Costo por clic (CPC)', v: `$${c.cpc.toFixed(2)}`, s: 'promedio del periodo' },
    ].map((k) => `<div class="kpi"><div class="l">${esc(k.l)}</div><div class="v">${esc(k.v)}</div><div class="s">${esc(k.s)}</div></div>`).join('');

    const objRows = byObjective.map((g) => `<tr><td>${esc(g.key)}</td><td class="num">${esc(money(g.spend))}</td><td class="num">${esc(pct(g.spend / (c.spend || 1), 0))}</td></tr>`).join('');

    const fig = (title, src) => src ? `<div class="fig"><div class="ft">${esc(title)}</div><img src="${src}" alt="${esc(title)}"></div>` : '';
    const figWide = (title, src) => src ? `<div class="fig wide"><div class="ft">${esc(title)}</div><img src="${src}" alt="${esc(title)}"></div>` : '';

    // Resumen ejecutivo: cada gráfica como figura + su fila de tiles (HTML nítido).
    const execFig = (card) => `<div class="exec-fig${card.wide ? ' wide' : ''}">
      <div class="ft">${esc(card.title)}</div>
      ${card.img ? `<img src="${card.img}" alt="${esc(card.title)}">` : ''}
      <div class="exec-pdf-tiles">${card.tiles.map((t) => `<div class="exec-pdf-tile" style="border-left-color:${esc(t.color)}"><div class="tl">${esc(t.label)}</div><div class="tv">${esc(t.value)}</div><div class="ts">${esc(t.sub)}</div></div>`).join('')}</div>
      ${card.formula ? `<div class="exec-pdf-formula"><b>ƒ</b> ${esc(card.formula)}</div>` : ''}
    </div>`;
    const execSection = exec && exec.cards.length
      ? `<div class="sec-title">Resumen ejecutivo${exec.period ? ` — ${esc(exec.period)}` : ''}</div><div class="exec-charts">${exec.cards.map(execFig).join('')}</div>`
      : '';

    // Secciones de Meta (lo que se ve en el análisis por pestañas), como tablas
    // HTML: se imprimen nítidas y no dependen de que una gráfica esté montada.
    const metaSection = (() => {
      const d = isMeta ? analysis.data : null;
      if (!d) return '';
      const t = d.totals || {};
      const mm = metaMoney;
      const tbl = (title, head, rows) => (rows.length
        ? `<div class="fig"><div class="ft">${esc(title)}</div><table class="mini"><thead><tr>${head.map((h, i) => `<th${i ? ' class="num"' : ''}>${esc(h)}</th>`).join('')}</tr></thead><tbody>${rows.map((r) => `<tr>${r.map((v, i) => `<td${i ? ' class="num"' : ''}>${esc(v)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>`
        : '');
      const cards = [
        t.result?.value != null && { l: t.result.label, v: compact(t.result.value), s: t.result.cost != null ? `${mm.unit(t.result.cost)} ${t.result.perMil ? 'por mil' : 'c/u'}` : '' },
        { l: 'Personas alcanzadas', v: compact(t.reach), s: 'únicas en el periodo' },
        { l: 'Frecuencia', v: t.frequency != null ? t.frequency.toFixed(2) : '—', s: 'impresiones por persona' },
        { l: 'CTR de salida', v: t.outboundCtr != null ? pct(t.outboundCtr) : '—', s: `CTR total ${t.ctr != null ? pct(t.ctr) : '—'}` },
        t.roas != null ? { l: 'ROAS', v: `${t.roas.toFixed(2)}x`, s: `${mm(t.purchaseValue)} en ventas` } : { l: 'Clics de salida', v: compact(t.outboundClicks), s: `de ${compact(t.clicks)} clics` },
      ].filter(Boolean).map((k) => `<div class="kpi"><div class="l">${esc(k.l)}</div><div class="v">${esc(k.v)}</div><div class="s">${esc(k.s)}</div></div>`).join('');
      const bd = d.breakdowns || {};
      const G = { male: 'Hombres', female: 'Mujeres', unknown: 'Sin especificar' };
      const seg = [...(bd.ageGender || [])].sort((a, b) => (b.spend || 0) - (a.spend || 0)).slice(0, 8)
        .map((r) => [`${G[r.dims?.gender] || r.dims?.gender} ${r.dims?.age}`, mm(r.spend), compact(r.impressions), r.ctr != null ? pct(r.ctr) : '—']);
      const reg = (bd.region || []).slice(0, 8).map((r) => [r.dims?.region || '—', mm(r.spend), compact(r.reach), r.ctr != null ? pct(r.ctr) : '—']);
      const plc = (bd.platform || []).slice(0, 8).map((r) => [`${r.dims?.publisher_platform || ''} · ${(r.dims?.platform_position || '').replace(/_/g, ' ')}`, mm(r.spend), compact(r.impressions), r.ctr != null ? pct(r.ctr) : '—']);
      const act = (d.actionCatalog || []).slice(0, 10).map((a) => [a.label, numFmt.format(Math.round(a.total || 0)), a.cost != null ? mm.unit(a.cost) : '—']);
      const ads = (d.creatives || []).slice(0, 10).map((c2) => [c2.title || c2.name, mm(c2.spend),
        c2.result?.value != null ? `${compact(c2.result.value)} · ${c2.result.cost != null ? mm.unit(c2.result.cost) : '—'}` : '—',
        pct(c2.outboundCtr ?? c2.ctr), ({ ABOVE_AVERAGE: 'Superior', AVERAGE: 'Promedio' })[c2.quality_ranking] || (c2.quality_ranking ? 'Inferior' : '—')]);
      const health = (d.health?.items || explore.data?.health?.items || []);
      const healthHtml = health.length
        ? `<div class="fig wide"><div class="ft">Salud de la entrega</div><table class="mini"><tbody>${health.slice(0, 12).map((i) => `<tr><td>${esc(i.level)}: <b>${esc(i.entity || '')}</b></td><td>${esc(i.message)}</td></tr>`).join('')}</tbody></table></div>`
        : '';
      return `<div class="sec-title">Análisis de Meta${focus ? ` — ${esc(focus.name)}` : ''}</div>
        <div class="kpis">${cards}</div>
        <div class="charts" style="margin-top:10px">
          ${healthHtml}
          ${tbl('Audiencia: edad y género', ['Segmento', 'Inversión', 'Impresiones', 'CTR'], seg)}
          ${tbl('Estados con mayor inversión', ['Estado', 'Inversión', 'Alcance', 'CTR'], reg)}
          ${tbl('Red y ubicación', ['Ubicación', 'Inversión', 'Impresiones', 'CTR'], plc)}
          ${tbl('Acciones atribuidas', ['Acción', 'Total', 'Costo'], act)}
          ${ads.length ? `<div class="fig wide"><div class="ft">Anuncios con mayor inversión</div><table class="mini"><thead><tr><th>Anuncio</th><th class="num">Inversión</th><th class="num">Resultado · costo</th><th class="num">CTR</th><th class="num">Calidad</th></tr></thead><tbody>${ads.map((r) => `<tr>${r.map((v, i) => `<td${i ? ' class="num"' : ''}>${esc(v)}</td>`).join('')}</tr>`).join('')}</tbody></table></div>` : ''}
        </div>`;
    })();

    const detailRows = tableRows.map((r) => `<tr>
      ${multiMonth ? `<td>${esc(rowMonthLabel(r))}</td>` : ''}
      <td>${esc(r.platform)}</td>
      <td>${esc(r.campaign)}</td>
      <td>${esc(r.objectiveRaw || r.objective)}</td>
      <td>${esc(r.buyType)}</td>
      <td class="num">${r.impressions != null ? numFmt.format(r.impressions) : '—'}</td>
      <td class="num">${r.clicks != null ? numFmt.format(r.clicks) : '—'}</td>
      <td class="num">${r.ctr != null ? pct(r.ctr) : '—'}</td>
      <td class="num">${r.spend != null ? money(r.spend) : '—'}</td>
      ${hasResults ? `<td class="num">${r.results != null ? esc(`${numFmt.format(Math.round(r.results))} ${(r.resultLabel || '').toLowerCase()}`) : '—'}</td><td class="num">${r.costPerResult != null ? esc(metaMoney.unit(r.costPerResult)) : '—'}</td>` : ''}
      <td class="num">${r.index != null ? pct(r.index, 0) : '—'}</td>
      <td>${esc(r.status)}</td>
    </tr>`).join('');

    const fullHtml = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"><title>Reporte de Campañas — ${esc(orgName)}</title>
<style>
  @page { size: A4; margin: 1.2cm 1.2cm; }
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #1e293b; margin: 0; background: #fff; font-size: 12px; }
  .doc-header { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 12px; border-bottom: 3px solid #073C5C; margin-bottom: 14px; }
  .doc-header-left { display: flex; align-items: center; gap: 12px; }
  .doc-header img.logo { height: 40px; width: auto; object-fit: contain; }
  .org-name { font-weight: 800; font-size: 15px; color: #073C5C; }
  .org-sub { font-size: 10px; color: #64748b; text-transform: uppercase; letter-spacing: .06em; }
  .doc-header-right { text-align: right; }
  .date-label { font-size: 9px; color: #94a3b8; text-transform: uppercase; letter-spacing: .08em; }
  .date { font-size: 11px; color: #073C5C; font-weight: 700; }
  h1.title { font-size: 21px; font-weight: 800; color: #073C5C; margin: 0 0 4px; letter-spacing: -.02em; }
  .meta { font-size: 11px; color: #64748b; margin-bottom: 14px; padding-bottom: 10px; border-bottom: 1px solid #e2e8f0; }
  .meta strong { color: #1e293b; }
  .sec-title { font-size: 11px; font-weight: 800; color: #64748b; text-transform: uppercase; letter-spacing: .05em; margin: 16px 0 8px; }
  .kpis { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8px; }
  .kpi { border: 1px solid #e2e8f0; border-radius: 8px; padding: 9px 11px; }
  .kpi .l { font-size: 8.5px; text-transform: uppercase; color: #64748b; letter-spacing: .05em; font-weight: 700; }
  .kpi .v { font-size: 17px; font-weight: 800; color: #073C5C; margin-top: 2px; }
  .kpi .s { font-size: 8.5px; color: #94a3b8; margin-top: 2px; }
  .charts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .fig { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; page-break-inside: avoid; }
  .fig.wide { grid-column: 1 / -1; }
  .fig .ft { font-size: 9.5px; text-transform: uppercase; font-weight: 800; color: #64748b; letter-spacing: .04em; margin-bottom: 6px; }
  .fig img { width: 100%; height: auto; display: block; }
  .exec-charts { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
  .exec-fig { border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 10px; page-break-inside: avoid; }
  .exec-fig .ft { font-size: 9.5px; text-transform: uppercase; font-weight: 800; color: #64748b; letter-spacing: .04em; margin-bottom: 6px; }
  .exec-fig img { width: 100%; height: auto; display: block; }
  .exec-fig.wide { grid-column: 1 / -1; }
  .exec-fig.wide .exec-pdf-tiles { grid-template-columns: repeat(5, 1fr); }
  .exec-pdf-tiles { display: grid; grid-template-columns: repeat(2, 1fr); gap: 5px; margin-top: 8px; }
  .exec-pdf-tile { border: 1px solid #eef2f6; border-left: 3px solid #073C5C; border-radius: 5px; padding: 5px 7px; }
  .exec-pdf-tile .tl { font-size: 7px; text-transform: uppercase; color: #64748b; font-weight: 700; letter-spacing: .03em; }
  .exec-pdf-tile .tv { font-size: 13px; font-weight: 800; color: #073C5C; margin: 1px 0; }
  .exec-pdf-tile .ts { font-size: 7px; color: #94a3b8; }
  .exec-pdf-formula { margin-top: 6px; padding-top: 5px; border-top: 1px dashed #e2e8f0; font-size: 7.5px; color: #64748b; line-height: 1.4; }
  .exec-pdf-formula b { color: #073C5C; font-style: italic; }
  table { width: 100%; border-collapse: collapse; }
  table.mini { font-size: 10px; }
  table.mini td { border-bottom: 1px solid #eef2f6; padding: 4px 6px; }
  table.mini td.num { text-align: right; font-weight: 600; }
  table.mini th { text-align: left; font-size: 8px; text-transform: uppercase; color: #64748b; padding: 3px 6px; border-bottom: 1px solid #e2e8f0; }
  table.mini th.num { text-align: right; }
  table.detail { font-size: 9px; margin-top: 4px; }
  table.detail thead { display: table-header-group; }
  table.detail tr { page-break-inside: avoid; }
  table.detail th { background: #073C5C; color: #fff; padding: 5px 6px; text-align: left; font-size: 8.5px; text-transform: uppercase; letter-spacing: .02em; }
  table.detail td { border-bottom: 1px solid #eef2f6; padding: 4px 6px; }
  table.detail th.num, table.detail td.num { text-align: right; }
  .doc-footer { margin-top: 20px; padding-top: 8px; border-top: 1px solid #e2e8f0; display: flex; justify-content: space-between; font-size: 9px; color: #94a3b8; letter-spacing: .03em; }
</style></head><body>
  <header class="doc-header">
    <div class="doc-header-left">
      <img class="logo" src="${esc(orgLogo)}" alt="${esc(orgName)}" onerror="this.style.display='none'">
      <div><div class="org-name">${esc(orgName)}</div><div class="org-sub">Reporte de Campañas Digitales</div></div>
    </div>
    <div class="doc-header-right"><div class="date-label">Generado</div><div class="date">${esc(generatedDate)}</div></div>
  </header>
  <h1 class="title">Campañas Digitales</h1>
  <div class="meta">Periodo: <strong>${esc(periodLabel)}</strong> · Plataforma: <strong>${esc(platLabel)}</strong>${cutoff ? ` · Fecha de corte: <strong>${esc(cutoff)}</strong>` : ''} · ${esc(tableRows.length)} líneas</div>

  <div class="sec-title">Indicadores clave</div>
  <div class="kpis">${kpiCards}</div>

  ${execSection}

  <div class="sec-title">Visión general</div>
  <div class="charts">
    ${fig('Gasto por plataforma', imgs.donut)}
    ${fig('Inversión mensual: real vs plan', imgs.spend)}
    ${figWide('Top 10 campañas por inversión', imgs.top)}
    ${fig('Clics y tasa de clics (CTR) por mes', imgs.clicks)}
    <div class="fig"><div class="ft">Gasto por objetivo</div><table class="mini"><tbody>${objRows}</tbody></table></div>
  </div>

  ${metaSection}

  <div class="sec-title">Detalle por campaña${daysMode ? ` — ${esc(monthsPeriodLabel)}` : ''}</div>
  <table class="detail"><thead><tr>
    ${multiMonth ? '<th>Mes</th>' : ''}<th>Plataforma</th><th>Campaña</th><th>Objetivo</th><th>Compra</th>
    <th class="num">Impresiones</th><th class="num">Clics</th><th class="num">Tasa de clics</th><th class="num">Gasto</th>${hasResults ? '<th class="num">Resultado</th><th class="num">Costo/res.</th>' : ''}<th class="num">Ejecución</th><th>Estado</th>
  </tr></thead><tbody>${detailRows}</tbody></table>

  <footer class="doc-footer"><span>${esc(orgName)} · Documento confidencial</span><span>Generado automáticamente desde el tablero de Campañas</span></footer>
</body></html>`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute('aria-hidden', 'true');
    iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;';
    document.body.appendChild(iframe);
    setExporting(true);
    const cleanup = () => { setExporting(false); setTimeout(() => { try { document.body.removeChild(iframe); } catch (e) {} }, 1500); };
    const triggerPrint = () => {
      try { const win = iframe.contentWindow; if (!win) { cleanup(); return; } win.focus(); win.print(); }
      catch (err) { console.error('[CampaignsPage] Error al imprimir:', err); }
      finally { cleanup(); }
    };
    iframe.onload = () => {
      const doc = iframe.contentDocument;
      if (!doc) { cleanup(); return; }
      const pending = Array.from(doc.images || []).filter((im) => !im.complete);
      if (pending.length === 0) { setTimeout(triggerPrint, 120); return; }
      let left = pending.length;
      const dec = () => { if (--left <= 0) setTimeout(triggerPrint, 120); };
      pending.forEach((im) => { im.addEventListener('load', dec, { once: true }); im.addEventListener('error', dec, { once: true }); });
      setTimeout(() => { if (left > 0) triggerPrint(); }, 4000);
    };
    iframe.srcdoc = fullHtml;
  };

  return (
    <main className="cmp-page">
      {/* ═══ HEADER ═══
          Siempre visible (también cargando o con error) para que el selector de
          red y la cuenta nunca desaparezcan: si TikTok no responde, el usuario
          vuelve a Meta desde aquí sin salir de la pantalla. */}
      <header className="cmp-header">
        <div className="cmp-header-left">
          <span className="cmp-title-icon"><FaBullhorn /></span>
          <div>
            <h1 className="cmp-title">
              Campañas Digitales
              {isLive && <span className="cmp-live"><span className="cmp-live-dot" />En vivo</span>}
            </h1>
            <div className="cmp-subtitle">
              {ready
                ? <>{state.brand} · {periodLabel}{cutoff ? <> · Datos al <strong>{cutoffLabel(cutoff)}</strong></> : null}</>
                : isLive ? `Datos en vivo desde ${liveLabel}` : 'Reportes mensuales'}
            </div>
          </div>
        </div>
        <div className="cmp-header-actions">
          {!isLive && ready && <span className="cmp-pill-info"><FaLayerGroup /> {months.length} reportes</span>}
          <button className="cmp-btn" onClick={() => { load(true); setRefreshTick((t) => t + 1); }} disabled={state.loading} title={isMeta ? 'Vuelve a consultar a Meta sin usar la copia de los últimos 10 minutos' : undefined}><FaSync className={state.loading ? 'cmp-spin' : undefined} /> Actualizar</button>
          <button className="cmp-btn" onClick={handleCsv} disabled={!ready} title="Descarga el detalle por campaña en CSV (se abre en Excel)"><FaFileCsv /> CSV</button>
          <button className="cmp-btn cmp-btn-accent" onClick={handleExport} disabled={exporting || !ready}>
            {exporting ? <><FaSpinner className="cmp-spin" /> Generando…</> : <><FaFilePdf /> Exportar PDF</>}
          </button>
        </div>
      </header>

      {/* ═══ FILTROS — en orden de jerarquía ═══
          1) Red publicitaria: cambia la fuente completa.
          2) Cuenta: solo si la red tiene más de una.
          3) Periodo: recorta lo ya cargado.
          Los filtros finos (objetivo, estado, búsqueda) viven en la tabla, que
          es lo único que afectan. */}
      <div className="cmp-toolbar">
        {showNetworkSwitch && (
          <div className="cmp-field">
            <span className="cmp-field-label">Red publicitaria</span>
            <div className="cmp-segmented" role="tablist" aria-label="Red publicitaria">
              {networks.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  role="tab"
                  aria-selected={source === n.id}
                  className={`cmp-seg net-${n.id} ${source === n.id ? 'active' : ''}`}
                  onClick={() => source !== n.id && onNetworkChange(n.id)}
                >
                  {NETWORK_ICONS[n.id]}{n.label}
                </button>
              ))}
            </div>
          </div>
        )}
        {showNetworkSwitch && <div className="cmp-toolbar-sep" />}

        {/* Selector de cuenta publicitaria: solo si hay mas de una dada de alta. */}
        {isMeta && accounts.length > 1 && (
          <label className="cmp-field">
            <span className="cmp-field-label">Cuenta</span>
            <select className="cmp-field-select" value={account || ''} onChange={(e) => setAccount(e.target.value)}>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.credential ? ` · ${a.credential}` : ''}{a.error ? ' (sin acceso)' : ''}
                </option>
              ))}
            </select>
          </label>
        )}

        {/* Periodo: un solo selector (Todos / rango rápido / mes específico / personalizado) */}
        <label className="cmp-field">
          <span className="cmp-field-label">Periodo</span>
          <select
            className="cmp-field-select cmp-field-period"
            disabled={state.loading || !!state.error}
            value={
              period.mode === 'days' ? `days:${period.days}`
                : period.mode === 'all' ? 'all'
                : period.mode === 'month' ? `month:${period.key}`
                  : period.preset === 'custom' ? 'custom'
                    : `preset:${period.preset}`
            }
            onChange={(e) => {
              const v = e.target.value;
              if (v === 'all') setPeriod({ mode: 'all' });
              else if (v.startsWith('days:')) setPeriod({ mode: 'days', days: Number(v.slice(5)) });
              else if (v === 'custom') enterCustom();
              else if (v.startsWith('preset:')) applyPreset(v.slice(7));
              else if (v.startsWith('month:')) setPeriod({ mode: 'month', key: v.slice(6) });
            }}
          >
            {isMeta && (
              <optgroup label="Días exactos">
                {DAY_PRESETS.map((d) => <option key={d} value={`days:${d}`}>{`Últimos ${d} días`}</option>)}
              </optgroup>
            )}
            <option value="all">Todos los meses</option>
            <optgroup label="Rango rápido (meses completos)">
              {PERIOD_PRESETS.map((p) => <option key={p.id} value={`preset:${p.id}`}>{p.label}</option>)}
            </optgroup>
            <optgroup label="Mes específico">
              {months.map((m) => <option key={m.key} value={`month:${m.key}`}>{`${m.month} ${m.year}`}</option>)}
            </optgroup>
            <option value="custom">Personalizado…</option>
          </select>
        </label>
        {period.mode === 'range' && period.preset === 'custom' && (
          <div className="cmp-daterange">
            <label className="cmp-date-field">Desde
              <input type="date" value={period.from || ''} min={dataMin} max={period.to || dataMax}
                onChange={(e) => setPeriod((p) => ({ mode: 'range', preset: 'custom', from: e.target.value || null, to: p.to ?? null }))} />
            </label>
            <span className="cmp-date-dash">–</span>
            <label className="cmp-date-field">Hasta
              <input type="date" value={period.to || ''} min={period.from || dataMin} max={dataMax}
                onChange={(e) => setPeriod((p) => ({ mode: 'range', preset: 'custom', from: p.from ?? null, to: e.target.value || null }))} />
            </label>
            <span className="cmp-date-hint" title="La fuente es mensual: se incluyen los meses completos que toquen el rango; no hay desglose por día.">detalle mensual</span>
          </div>
        )}

        {/* Comparar: solo con un periodo acotado (con "Todos" no hay contra qué). */}
        {comparable && !isTikTok && (
          <label className="cmp-field">
            <span className="cmp-field-label">Comparar con</span>
            <select className="cmp-field-select" value={compare} onChange={(e) => setCompare(e.target.value)}>
              {COMPARE_OPTS.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </label>
        )}

        {/* Plataforma: solo cuando la fuente mezcla varias (Excel, demo). */}
        {!singleSource && ready && platforms.length > 0 && (
          <>
            <div className="cmp-toolbar-sep" />
            <div className="cmp-field">
              <span className="cmp-field-label">Plataforma</span>
              <div className="cmp-chips">
                <button className={`cmp-chip ${platform === 'all' ? 'active' : ''}`} style={platform === 'all' ? { background: '#073C5C' } : undefined} onClick={() => setPlatform('all')}>Todas</button>
                {platforms.map((p) => (
                  <button key={p} className={`cmp-chip ${platform === p ? 'active' : ''}`} style={platform === p ? { background: PLATFORM_COLORS[p] } : undefined} onClick={() => setPlatform(p)}>
                    <span className="chip-dot" style={{ background: platform === p ? '#fff' : PLATFORM_COLORS[p] }} />{p}
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {/* ═══ ESTADOS ═══ */}
      {(state.loading || daysLoading) && (
        <div className="cmp-state">
          <FaSpinner className="big cmp-spin" />
          <h3>{isLive ? `Consultando campañas en ${liveLabel}…` : 'Cargando reportes de campañas…'}</h3>
          <p>{isLive ? 'Esto puede tardar unos segundos.' : 'Leyendo los Excel mensuales y normalizando los datos.'}</p>
        </div>
      )}

      {state.error && isTikTok && state.errorKind === 'fail' && (
        // TikTok todavía no está conectado: se explica en lenguaje de negocio,
        // con salida directa a Meta. El mensaje técnico queda plegado.
        <div className="cmp-state cmp-state-card">
          <span className="cmp-state-badge net-tiktok"><FaTiktok /></span>
          <h3>Las campañas de TikTok aún no están disponibles</h3>
          <p>Estamos terminando de conectar la cuenta publicitaria de TikTok. Cuando quede lista, aquí verás sus campañas con los mismos indicadores que ya tienes para Meta.</p>
          <div className="cmp-state-actions">
            {showNetworkSwitch && <button className="cmp-btn cmp-btn-accent" onClick={() => onNetworkChange('meta')}><FaMeta /> Ver campañas de Meta</button>}
            <button className="cmp-btn" onClick={() => load(true)}><FaSync /> Reintentar</button>
          </div>
          <details className="cmp-tech"><summary>Detalle técnico</summary>{state.error}</details>
        </div>
      )}

      {state.error && !(isTikTok && state.errorKind === 'fail') && (
        // Con varias cuentas, un fallo NO puede dejar la pantalla sin salida: el
        // selector de cuenta sigue arriba para cambiar a otra.
        <>
          <div className="cmp-state">
            <FaExclamationTriangle className="big" />
            <h3>{state.errorKind === 'empty' ? 'No hay campañas para mostrar' : 'No se pudo cargar el tablero'}</h3>
            <p>{state.error}</p>
            <button className="cmp-btn cmp-btn-accent" onClick={() => load(true)}><FaSync /> Reintentar</button>
          </div>
        </>
      )}

      {ready && <>
      {state.errors?.length > 0 && !daysMode && (
        isLive ? (
          // En vivo el aviso dice el MOTIVO: antes solo nombraba "Meta" y los
          // ceros en interacciones o presupuesto quedaban sin explicación.
          <details className="cmp-errbar cmp-errbar-details">
            <summary><FaExclamationTriangle /> Parte de los datos llegó incompleta ({state.errors.length}). Ver motivo</summary>
            <ul>{state.errors.map((e, i) => <li key={i}>{e.error || e.month}</li>)}</ul>
          </details>
        ) : (
          <div className="cmp-errbar"><FaExclamationTriangle /> No se pudieron leer {state.errors.length} reporte(s): {state.errors.map((e) => e.month).join(', ')}.</div>
        )
      )}

      {/* Ficha de la cuenta + salud de la entrega (solo Meta). */}
      {isMeta && (
        <AccountStrip account={explore.data?.account} health={explore.data?.health} money={metaMoney} loading={explore.loading} />
      )}

      {daysMode && daysData.error && (
        <div className="cmp-errbar"><FaExclamationTriangle /> No se pudieron cargar los últimos {period.days} días: {daysData.error}</div>
      )}
      {daysMode && daysData.errors.length > 0 && (
        <details className="cmp-errbar cmp-errbar-details">
          <summary><FaExclamationTriangle /> Parte de los datos de los últimos {period.days} días llegó incompleta. Ver motivo</summary>
          <ul>{daysData.errors.map((e, i) => <li key={i}>{e.error || e.month}</li>)}</ul>
        </details>
      )}

      {activeMonths.length === 0 && !(daysMode && daysData.error) && (
        <div className="cmp-errbar"><FaExclamationTriangle /> {daysMode
          ? `No hubo anuncios con entrega en los últimos ${period.days} días. Prueba con un periodo más largo o con "Todos los meses".`
          : 'El rango seleccionado no incluye ningún reporte mensual. Ajusta las fechas.'}</div>
      )}

      {/* ═══ KPIs PRIMARIOS ═══ */}
      <section className="cmp-kpis">
        <Kpi accent="#073C5C" icon={<FaDollarSign />} label="Gasto total" value={money(c.spend)} delta={delta(kpis, 'spend')} vs={compareShort} sub={c.spendPlanned ? `${pct(c.pacing, 1)} del plan` : 'sin presupuesto planeado'} />
        <Kpi accent="#3b82f6" icon={<FaEye />} label="Impresiones" value={compact(c.impressions)} delta={delta(kpis, 'impressions')} vs={compareShort} sub={`Alcance ${compact(dedupReach ?? c.reach)}`} />
        <Kpi accent="#10b981" icon={<FaMousePointer />} label="Clics" value={numFmt.format(c.clicks)} delta={delta(kpis, 'clicks')} vs={compareShort} sub={`${c.campaigns} campañas`} />
        <Kpi accent="#F68D2E" icon={<FaChartLine />} label="Tasa de clics (CTR)" value={pct(c.ctr)} delta={deltaPP(kpis, 'ctr')} vs={compareShort} sub={`${compact(c.interactions)} interacciones`} />
        <Kpi accent="#8b5cf6" icon={<FaTag />} label="Costo por mil impresiones (CPM)" value={money(c.cpm)} delta={delta(kpis, 'cpm', true)} vs={compareShort} sub="promedio del periodo" />
        <Kpi accent="#ec4899" icon={<FaHandPointer />} label="Costo por clic (CPC)" value={`$${c.cpc.toFixed(2)}`} delta={delta(kpis, 'cpc', true)} vs={compareShort} sub="promedio del periodo" />
      </section>

      {/* ═══ STATS SECUNDARIOS ═══ */}
      {isMeta ? (
        // Meta: el alcance es de personas únicas y se agregan frecuencia y
        // retorno, que solo la API conoce. El retorno aparece si hay ventas
        // atribuidas (píxel de compra); si no, se muestran las interacciones.
        <section className="cmp-substats">
          <Stat v={c.spendPlanned ? money(c.spendPlanned) : '—'} l="Inversión planeada" t="Presupuesto de Meta prorrateado a los días del periodo (a la fecha de corte en el mes en curso)" />
          <Stat v={c.spendPlanned ? pct(c.pacing, 1) : '—'} l="Ejecución vs plan" />
          <Stat v={compact(dedupReach ?? c.reach)} l={dedupReach != null ? 'Personas alcanzadas' : 'Alcance (suma)'} t={dedupReach != null ? 'Personas únicas en el periodo, sin contar dos veces a quien vio varias campañas' : 'Suma por campaña y mes; el dato sin duplicar está cargando'} />
          <Stat v={explore.data?.totals?.frequency != null ? explore.data.totals.frequency.toFixed(2) : '—'} l="Frecuencia" t="Veces que cada persona vio los anuncios, en promedio" />
          {explore.data?.totals?.roas != null
            ? <Stat v={`${explore.data.totals.roas.toFixed(2)}x`} l="Retorno (ROAS)" t="Ventas atribuidas / inversión" />
            : <Stat v={compact(c.interactions)} l="Interacciones" />}
          <Stat v={compact(c.views)} l="Reproducciones de video" />
        </section>
      ) : (
        <section className="cmp-substats">
          <Stat v={money(c.spendPlanned)} l="Inversión planeada" />
          <Stat v={pct(c.pacing, 1)} l="Ejecución vs plan" />
          <Stat v={compact(c.reach)} l="Alcance" />
          <Stat v={compact(c.interactions)} l="Interacciones" />
          <Stat v={compact(c.views)} l="Reproducciones de video" />
          <Stat v={c.campaigns} l="Campañas" />
        </section>
      )}

      {/* Evolución diaria (Meta): lo único del tablero con grano de día. */}
      {isMeta && (
        <div className="cmp-grid cmp-grid-gap">
          <DailyTrendCard daily={analysis.data?.daily || []} money={metaMoney} loading={analysis.loading}
            focusName={focus?.name} resultLabel={focus ? analysis.data?.totals?.result?.label : null} />
        </div>
      )}

      {/* ═══ RESUMEN EJECUTIVO (gráficas replicadas) ═══ */}
      <ExecutiveSummary ref={execRef} trend={trend} campaigns={byCampaign} periodDays={periodDays} platformLabel={singleSource ? liveLabel : platform === 'all' ? 'Todas' : platform} reachOverride={daysMode ? null : dedupReach} currency={isMeta ? currency : 'MXN'} campaignLabel={isMeta ? campName : null} periodText={isMeta ? monthsPeriodLabel : null} />

      {/* ═══ GRÁFICAS ═══ */}
      <div className="cmp-grid">
        {/* Plataforma */}
        <Card className="col-4" title="Gasto por plataforma" icon={<FaLayerGroup />} tag={multiMonth ? 'Acumulado' : firstActive?.month}>
          <div className="cmp-donut-wrap">
            <div className="cmp-donut-chart">
              <Doughnut ref={donutRef} data={donutData(byPlatform, (k) => PLATFORM_COLORS[k] || '#94a3b8')} options={donutOpts} />
              <div className="cmp-donut-center"><span className="t">Total</span><span className="v">{moneyCompact(c.spend)}</span></div>
            </div>
            <div className="cmp-legend">
              {byPlatform.map((g) => (
                <div className="cmp-leg" key={g.key}>
                  <span className="cmp-leg-dot" style={{ background: PLATFORM_COLORS[g.key] || '#94a3b8' }} />
                  <span className="cmp-leg-name">{g.key}</span>
                  <span className="cmp-leg-val">{moneyCompact(g.spend)}<span className="cmp-leg-pct">{pct(g.spend / (c.spend || 1), 0)}</span></span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Inversión mensual */}
        <Card className="col-4" title="Inversión mensual: real vs plan" icon={<FaChartBar />} tag={daysMode ? 'Meses completos' : 'Eje temporal'}>
          <div className="cmp-chart-box"><Bar ref={spendRef} data={spendTrendData(trend)} options={spendTrendOpts} /></div>
        </Card>

        {/* Objetivo */}
        <Card className="col-4" title={hasResults ? 'Gasto y resultado por objetivo' : 'Gasto por objetivo'} icon={<FaBullseye />}>
          <div className="cmp-hbars">
            {byObjective.length === 0 ? <div className="cmp-empty">Sin datos.</div> : byObjective.map((g) => {
              const max = byObjective[0].spend || 1;
              return (
                <div key={g.key}>
                  <div className="cmp-hbar-top">
                    <span className="cmp-hbar-name">{g.key}</span>
                    <span className="cmp-hbar-val">{moneyCompact(g.spend)}<span className="pct">{pct(g.spend / (c.spend || 1), 0)}</span></span>
                  </div>
                  <div className="cmp-hbar-track"><div className="cmp-hbar-fill" style={{ width: `${(g.spend / max) * 100}%`, background: OBJECTIVE_COLORS[g.key] || '#94a3b8' }} /></div>
                  {g.result && (
                    <div className="cmp-hbar-res">
                      <b>{compact(g.result.results)}</b> {g.result.label.toLowerCase()}
                      {g.result.cost != null && <> · <b>{metaMoney.unit(g.result.cost)}</b> {g.result.perMil ? 'por mil' : 'c/u'}</>}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </Card>

        {/* Top campañas */}
        <Card className="col-8" title="Top 10 campañas por inversión" icon={<FaTrophy />}>
          <div className="cmp-chart-box tall"><Bar ref={topRef} data={topData(topCampaigns, campName)} options={topOpts} /></div>
        </Card>

        {/* Clics y CTR */}
        <Card className="col-4" title="Clics y tasa de clics (CTR) por mes" icon={<FaMousePointer />} tag={daysMode ? 'Meses completos' : undefined}>
          <div className="cmp-chart-box tall"><Bar ref={clicksRef} data={clicksCtrData(trend)} options={clicksCtrOpts} /></div>
        </Card>

        {/* Tabla */}
        <section className="cmp-card col-12">
          <div className="cmp-card-head">
            <span className="cmp-card-title"><FaLayerGroup /> Detalle por campaña</span>
            <span className="cmp-card-tag">{isMeta ? 'Clic en una campaña para ver sus conjuntos y anuncios · ' : ''}{monthsPeriodLabel}</span>
          </div>
          <div className="cmp-table-toolbar">
            <div className="cmp-search"><FaSearch /><input placeholder="Buscar campaña, plataforma u objetivo…" value={search} onChange={(e) => setSearch(e.target.value)} /></div>
            <span className="cmp-select">Objetivo
              <select value={objFilter} onChange={(e) => setObjFilter(e.target.value)}>
                <option value="all">Todos</option>
                {objectiveOptions.map((o) => <option key={o} value={o}>{o}</option>)}
              </select>
            </span>
            <span className="cmp-select">Estado
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">Todos</option>
                {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </span>
          </div>
          <div className="cmp-table-scroll">
            <table className="cmp-table">
              <thead>
                <tr>
                  {multiMonth && <th onClick={() => toggleSort('order')}>Mes {sortIcon('order')}</th>}
                  {!isMeta && <th onClick={() => toggleSort('platform')}>Plataforma {sortIcon('platform')}</th>}
                  <th onClick={() => toggleSort('campaign')}>Campaña {sortIcon('campaign')}</th>
                  <th onClick={() => toggleSort('objective')}>Objetivo {sortIcon('objective')}</th>
                  {!isMeta && <th className="num" onClick={() => toggleSort('costReal')}>Costo real {sortIcon('costReal')}</th>}
                  <th className="num" onClick={() => toggleSort('impressions')}>Impresiones {sortIcon('impressions')}</th>
                  <th className="num" onClick={() => toggleSort('clicks')}>Clics {sortIcon('clicks')}</th>
                  <th className="num" onClick={() => toggleSort('ctr')}>Tasa de clics {sortIcon('ctr')}</th>
                  <th className="num" onClick={() => toggleSort('spend')}>Gasto real {sortIcon('spend')}</th>
                  {hasResults && <th className="num" onClick={() => toggleSort('results')}>Resultado {sortIcon('results')}</th>}
                  {hasResults && <th className="num" onClick={() => toggleSort('costPerResult')}>Costo por resultado {sortIcon('costPerResult')}</th>}
                  <th onClick={() => toggleSort('index')}>Ejecución {sortIcon('index')}</th>
                  <th onClick={() => toggleSort('status')}>Estado {sortIcon('status')}</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr><td colSpan={(multiMonth ? 11 : 10) + (hasResults ? 2 : 0) - (isMeta ? 2 : 0)} className="cmp-empty">No hay campañas que coincidan con los filtros.</td></tr>
                ) : pageRows.map((r, i) => (
                  <tr key={i}
                    className={isMeta && r.campaignId ? 'cmp-row-click' : undefined}
                    onClick={isMeta && r.campaignId ? () => setDrawer({ id: r.campaignId, name: r.campaign, resultKey: r.resultKey }) : undefined}
                    title={isMeta && r.campaignId ? 'Ver conjuntos de anuncios y anuncios de esta campaña' : undefined}>
                    {multiMonth && <td>{rowMonthLabel(r)}</td>}
                    {!isMeta && <td><span className="cmp-pill" style={{ background: (PLATFORM_COLORS[r.platform] || '#94a3b8') + '1f', color: PLATFORM_COLORS[r.platform] || '#475569' }}><span className="pdot" style={{ background: PLATFORM_COLORS[r.platform] || '#94a3b8' }} />{r.platform}</span></td>}
                    <td className="camp" title={r.campaign}>{isMeta ? campName(r.campaign) : r.campaign}</td>
                    <td title={r.objectiveRaw || ''}>{isMeta ? (OBJECTIVE_LABEL[r.objectiveRaw] || r.objective) : (r.objectiveRaw || r.objective)}</td>
                    {!isMeta && <td className="num">{r.costReal != null ? `$${r.costReal.toFixed(2)}` : '—'}</td>}
                    <td className="num">{r.impressions != null ? numFmt.format(r.impressions) : '—'}</td>
                    <td className="num">{r.clicks != null ? numFmt.format(r.clicks) : '—'}</td>
                    <td className="num">{r.ctr != null ? pct(r.ctr) : '—'}</td>
                    <td className="num">{r.spend != null ? money(r.spend) : '—'}</td>
                    {hasResults && (
                      <td className="num" title={r.resultLabel || ''}>
                        {r.results != null ? <>{compact(r.results)} <span className="cmp-res-unit">{RESULT_UNIT[r.resultLabel] || (r.resultLabel || '').toLowerCase()}</span></> : '—'}
                      </td>
                    )}
                    {hasResults && <td className="num">{r.costPerResult != null ? `${metaMoney.unit(r.costPerResult)}${r.resultPerMil ? ' /mil' : ''}` : '—'}</td>}
                    <td>{r.index != null ? (
                      <span className="cmp-pace" title={r.budgetType === 'lifetime' ? LIFETIME_PACE_HINT : undefined}>
                        <span className="cmp-pace-track"><span className="cmp-pace-fill" style={{ width: `${Math.min(100, (r.index || 0) * 100)}%`, background: rowPaceColor(r) }} /></span>
                        <span className="cmp-pace-val" style={{ color: rowPaceColor(r) }}>{pct(r.index, 0)}</span>
                      </span>) : '—'}</td>
                    <td><span className={`cmp-status ${statusClass(r.status)}`}>{r.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="cmp-pager">
            <span className="cmp-pager-info">
              Mostrando {tableRows.length === 0 ? 0 : (pageSafe - 1) * perPage + 1}–{Math.min(pageSafe * perPage, tableRows.length)} de {tableRows.length} campañas · Gasto {money(tableTotals.spend)}
            </span>
            <div className="cmp-pager-ctrls">
              <button className="cmp-pg" disabled={pageSafe <= 1} onClick={() => setPage(pageSafe - 1)}><FaChevronLeft /></button>
              {pageList(pageSafe, totalPages).map((p, i) => p === '…'
                ? <span key={`d${i}`} className="cmp-pg dots">…</span>
                : <button key={p} className={`cmp-pg ${p === pageSafe ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>)}
              <button className="cmp-pg" disabled={pageSafe >= totalPages} onClick={() => setPage(pageSafe + 1)}><FaChevronRight /></button>
            </div>
            <span className="cmp-select">Filas
              <select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))}>
                {PER_PAGE_OPTS.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </span>
          </div>
        </section>

        {/* Bloques exclusivos de la API de Meta. No se pintan con otras fuentes
            porque el Excel mensual y TikTok no tienen estos desgloses. */}
        {isMeta && (
          <MetaInsightsSections
            data={analysis.data}
            loading={analysis.loading}
            error={analysis.error}
            money={metaMoney}
            campaigns={metaCampaignOptions}
            focus={focus}
            onFocus={focusCampaign}
            sectionRef={analysisRef}
          />
        )}

      </div>
      </>}

      {drawer && (
        <CampaignDrawer username={username} account={account} campaign={drawer} range={metaRange}
          money={metaMoney} onClose={() => setDrawer(null)} onFocus={focusCampaign} />
      )}
    </main>
  );
}

// ── Subcomponentes ───────────────────────────────────────────────────────────
function Kpi({ accent, icon, label, value, sub, delta, vs = 'mes ant.' }) {
  return (
    <div className="cmp-kpi" style={{ '--kpi-accent': accent }}>
      <div className="cmp-kpi-head">
        <span className="cmp-kpi-label">{label}</span>
        <span className="cmp-kpi-icon">{icon}</span>
      </div>
      <div className="cmp-kpi-value">{value}</div>
      <div className="cmp-kpi-foot">
        {delta
          ? <span className={`cmp-kpi-delta ${delta.dir === 'up' ? 'cmp-up' : delta.dir === 'down' ? 'cmp-down' : 'cmp-flat'}`}>{delta.dir === 'up' ? <FaArrowUp /> : delta.dir === 'down' ? <FaArrowDown /> : null}{delta.text} vs {vs}</span>
          : <span className="cmp-kpi-sub">{sub}</span>}
      </div>
    </div>
  );
}

function Stat({ v, l, t }) {
  return <div className="cmp-substat" title={t}><span className="v">{v}</span><span className="l">{l}</span></div>;
}

function Card({ className, title, icon, tag, children }) {
  return (
    <section className={`cmp-card ${className || ''}`}>
      <div className="cmp-card-head">
        <span className="cmp-card-title">{icon}{title}</span>
        {tag && <span className="cmp-card-tag">{tag}</span>}
      </div>
      <div className="cmp-card-body">{children}</div>
    </section>
  );
}

// ── Deltas vs mes anterior ───────────────────────────────────────────────────
// good=false: para CPM/CPC, subir es malo (invertimos el color).
function delta(kpis, key, lowerIsBetter = false) {
  if (!kpis.prev) return null;
  const a = kpis.cur[key] || 0, b = kpis.prev[key] || 0;
  if (!b) return null;
  const r = (a - b) / b;
  const rising = r > 0.001, falling = r < -0.001;
  const dir = !rising && !falling ? 'flat' : lowerIsBetter ? (rising ? 'down' : 'up') : (rising ? 'up' : 'down');
  return { dir, text: `${r > 0 ? '+' : ''}${(r * 100).toFixed(1)}%` };
}
function deltaPP(kpis, key) {
  if (!kpis.prev) return null;
  const d = (kpis.cur[key] || 0) - (kpis.prev[key] || 0);
  return { dir: d > 0.0001 ? 'up' : d < -0.0001 ? 'down' : 'flat', text: `${d > 0 ? '+' : ''}${(d * 100).toFixed(2)} pp` };
}

// ── Paginación: lista compacta con elipsis ───────────────────────────────────
function pageList(cur, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const lo = Math.max(2, cur - 1), hi = Math.min(total - 1, cur + 1);
  if (lo > 2) out.push('…');
  for (let p = lo; p <= hi; p++) out.push(p);
  if (hi < total - 1) out.push('…');
  out.push(total);
  return out;
}

// ── Builders Chart.js ─────────────────────────────────────────────────────────
function spendTrendData(trend) {
  return {
    labels: trend.map((t) => t.label),
    datasets: [
      { label: 'Planeada', data: trend.map((t) => Math.round(t.spendPlanned)), backgroundColor: '#d4dde6', borderRadius: 5, categoryPercentage: 0.62, barPercentage: 0.92 },
      { label: 'Real', data: trend.map((t) => Math.round(t.spend)), backgroundColor: '#073C5C', borderRadius: 5, categoryPercentage: 0.62, barPercentage: 0.92 },
    ],
  };
}
const spendTrendOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, padding: 10, font: { size: 10 } } },
    tooltip: { callbacks: { label: (c) => `${c.dataset.label}: ${money(c.parsed.y)}` } },
  },
  scales: { y: { beginAtZero: true, ticks: { callback: (v) => moneyCompact(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#f1f5f9' } }, x: { grid: { display: false }, ticks: { font: { size: 11 } } } },
};

function donutData(groups, colorFn) {
  return { labels: groups.map((g) => g.key), datasets: [{ data: groups.map((g) => Math.round(g.spend)), backgroundColor: groups.map((g) => colorFn(g.key)), borderColor: '#fff', borderWidth: 2, hoverOffset: 5 }] };
}
const donutOpts = {
  responsive: true, maintainAspectRatio: false, cutout: '68%',
  plugins: { legend: { display: false }, tooltip: { callbacks: { label: (c) => { const tot = c.dataset.data.reduce((a, b) => a + b, 0); return `${c.label}: ${money(c.parsed)} (${pct(c.parsed / (tot || 1), 0)})`; } } } },
};

function clicksCtrData(trend) {
  return {
    labels: trend.map((t) => t.label),
    datasets: [
      { type: 'bar', label: 'Clics', data: trend.map((t) => t.clicks), backgroundColor: '#10b981', borderRadius: 5, yAxisID: 'y', order: 2 },
      { type: 'line', label: 'Tasa de clics (CTR)', data: trend.map((t) => +(t.ctr * 100).toFixed(3)), borderColor: '#F68D2E', backgroundColor: '#F68D2E', tension: 0.35, pointRadius: 3, pointBackgroundColor: '#F68D2E', borderWidth: 2, yAxisID: 'y1', order: 1 },
    ],
  };
}
const clicksCtrOpts = {
  responsive: true, maintainAspectRatio: false,
  plugins: {
    legend: { position: 'top', align: 'end', labels: { boxWidth: 10, boxHeight: 10, padding: 10, font: { size: 10 } } },
    tooltip: { callbacks: { label: (c) => c.dataset.label === 'Tasa de clics (CTR)' ? `Tasa de clics (CTR): ${c.parsed.y}%` : `Clics: ${numFmt.format(c.parsed.y)}` } },
  },
  scales: {
    y: { beginAtZero: true, position: 'left', ticks: { callback: (v) => compact(v), font: { size: 10 }, maxTicksLimit: 5 }, grid: { color: '#f1f5f9' } },
    y1: { beginAtZero: true, position: 'right', ticks: { callback: (v) => `${v}%`, font: { size: 10 }, maxTicksLimit: 5 }, grid: { drawOnChartArea: false } },
    x: { grid: { display: false }, ticks: { font: { size: 11 } } },
  },
};

// Etiqueta del eje: sin el prefijo común y recortada (más en celular); el
// nombre completo va en el tooltip.
const TOP_LABEL_MAX = () => (typeof window !== 'undefined' && window.innerWidth < 640 ? 18 : 34);
const clip = (t, n) => (t && t.length > n ? `${t.slice(0, n - 1)}…` : t);
function topData(groups, nameFn = (x) => x) {
  const max = TOP_LABEL_MAX();
  return {
    labels: groups.map((g) => clip(nameFn(g.key), max)),
    datasets: [{ label: 'Inversión', fullNames: groups.map((g) => g.key), data: groups.map((g) => Math.round(g.spend)), backgroundColor: '#073C5C', borderRadius: 5, hoverBackgroundColor: '#F68D2E', barThickness: 'flex', maxBarThickness: 18 }],
  };
}
const topOpts = {
  indexAxis: 'y', responsive: true, maintainAspectRatio: false,
  plugins: { legend: { display: false }, tooltip: { callbacks: {
    title: (it) => it[0]?.dataset?.fullNames?.[it[0].dataIndex] || it[0]?.label || '',
    label: (c) => `Inversión: ${money(c.parsed.x)}`,
  } } },
  scales: { x: { beginAtZero: true, ticks: { callback: (v) => moneyCompact(v), font: { size: 10 } }, grid: { color: '#f1f5f9' } }, y: { ticks: { font: { size: 10.5 }, autoSkip: false }, grid: { display: false } } },
};
